import React, { useState, useEffect } from "react";
import "./ProfileModal.css";
import { useProfile } from "../context/ProfileContext";
import { useScrollLock } from "../hooks/useScrollLock";
import { useApi } from "../utils/Api";
import OrderHistory from "./OrderHistory";

const ProfileModal = ({ user, onLogout, onUpdate }) => {
  const {
    isProfileOpen,
    activeTab,
    closeProfile,
    setActiveTab: setContextTab,
  } = useProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState(""); // ✅ phone shown exactly as stored in DB
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { apiFetch } = useApi();

  useScrollLock(isProfileOpen);

  // ✅ Update form fields when user changes (phone directly from DB)
  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setEmail(user?.email || "");
    setPhone(user?.phone || "");
  }, [user]);

  const handleSave = async () => {
    if (!user?.id) return;

    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    const ph = String(phone ?? "").trim(); // ✅ keep DB format (just trim)

    // Required fields validation
    if (!fn || !ln || !em || !ph) {
      setError("All fields are required.");
      return;
    }

    // Basic email validation
    const emailOk = /^\S+@\S+\.\S+$/.test(em);
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await apiFetch("update-profile", {
        user_id: user.id,
        first_name: fn,
        last_name: ln,
        phone: ph, // ✅ send exactly what user sees (DB format)
        email: em,
      });

      if (data.status === "success") {
        // Update parent user state
        onUpdate({
          ...user,
          first_name: fn,
          last_name: ln,
          phone: ph,
          email: em,
        });

        closeProfile();
      } else {
        if (data.message?.includes("Phone")) {
          setError("Phone number is already in use.");
        } else if (data.message?.includes("Email")) {
          setError("Email is already in use.");
        } else {
          setError(data.message || "Failed to update profile.");
        }
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isProfileOpen || !user) return null;

  return (
    <div className="profile-modal-overlay" onClick={closeProfile}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <span className="profile-modal-close" onClick={closeProfile}>
          &times;
        </span>

        {/* Tabs */}
        <div className="profile-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setContextTab("profile")}
          >
            Profile
          </button>
          <button
            className={activeTab === "orders" ? "active" : ""}
            onClick={() => setContextTab("orders")}
          >
            Order History
          </button>
        </div>

        {/* PROFILE TAB */}
        {activeTab === "profile" && (
          <>
            <label>First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />

            <label>Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />

            <label>Phone</label>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="Enter phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="phone-input"
              required
            />

            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            {error && <p className="auth-error">{error}</p>}

            <div className="profile-modal-actions">
              <button onClick={handleSave} disabled={loading}>
                {loading ? <span className="spinner"></span> : "Save"}
              </button>

              <button className="logout-btn" onClick={onLogout}>
                Logout
              </button>
            </div>
          </>
        )}

        {/* ORDERS TAB */}
        {activeTab === "orders" && (
          <OrderHistory user={user} closeProfile={closeProfile} />
        )}
      </div>
    </div>
  );
};

export default ProfileModal;